
import { toast } from '../hooks/use-toast';
import { logger } from './logger';
import { AxiosError } from 'axios';
import { ErrorHandlerOptions, ErrorSeverity, ErrorType, StandardError } from '../types/error';

class ErrorService {
  private defaultOptions: ErrorHandlerOptions = {
    showToast: true,
    logError: true,
    severity: ErrorSeverity.MEDIUM
  };

  /**
   * Handles API errors from axios responses
   */
  handleApiError(error: AxiosError, options?: ErrorHandlerOptions): StandardError {
    const opts = { ...this.defaultOptions, ...options };
    const status = error.response?.status;
    const data = error.response?.data as { errors?: Array<{ reason: string }>; reason?: string; message?: string } | undefined;
    
    let title = 'Greška';
    let description = 'Došlo je do greške prilikom izvršavanja zahteva';
    let severity = ErrorSeverity.MEDIUM;
    
    // Handle multiple errors if they exist
    if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      const errorMessages = data.errors
        .map((err) => err.reason)
        .filter((reason) => reason)
        .join(' ');
      
      if (errorMessages) {
        description = errorMessages;
      }
    } else if (data?.reason) {
      description = data.reason;
    } else if (data?.message) {
      description = data.message;
    }
    
    switch (status) {
      case 400:
        title = 'Neispravni podaci';
        if (!data?.errors && !data?.reason && !data?.message) {
          description = 'Proverite unete podatke';
        }
        break;
      case 403:
        title = 'Nemate dozvolu';
        severity = ErrorSeverity.HIGH;
        if (!data?.errors && !data?.reason && !data?.message) {
          description = 'Nemate dozvolu za ovu akciju';
        }
        break;
      case 404:
        title = 'Nije pronađeno';
        if (!data?.errors && !data?.reason && !data?.message) {
          description = 'Traženi resurs nije pronađen';
        }
        break;
      case 409:
        title = 'Konflikt';
        if (!data?.errors && !data?.reason && !data?.message) {
          description = 'Resurs već postoji';
        }
        break;
      case 422:
        title = 'Validacijska greška';
        if (!data?.errors && !data?.reason && !data?.message) {
          description = 'Podaci nisu validni';
        }
        break;
      case 500:
        title = 'Greška servera';
        severity = ErrorSeverity.CRITICAL;
        description = 'Došlo je do greške na serveru. Pokušajte ponovo.';
        break;
      default:
        title = `Greška ${status}`;
        if (!data?.errors && !data?.reason && !data?.message) {
          description = 'Neočekivana greška';
        }
    }
    
    const standardError: StandardError = {
      type: ErrorType.API,
      severity: opts.severity || severity,
      message: `${title}: ${description}`,
      code: status,
      details: data,
      timestamp: new Date(),
      context: opts.context
    };
    
    if (opts.logError) {
      this.logError(standardError);
    }
    
    if (opts.showToast) {
      this.showErrorToast(title, description);
    }
    
    return standardError;
  }

  /**
   * Handles network errors (no response from server)
   */
  handleNetworkError(error: AxiosError, options?: ErrorHandlerOptions): StandardError {
    const opts = { ...this.defaultOptions, ...options };
    
    const standardError: StandardError = {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.HIGH,
      message: 'Greška mreže: Nema odgovora od servera. Proverite internetsku vezu.',
      details: error,
      timestamp: new Date(),
      context: opts.context
    };
    
    if (opts.logError) {
      this.logError(standardError);
    }
    
    if (opts.showToast) {
      this.showErrorToast('Greška mreže', 'Nema odgovora od servera. Proverite internetsku vezu.');
    }
    
    return standardError;
  }

  /**
   * Handles validation errors
   */
  handleValidationError(errors: Array<{ field: string; message: string }>, options?: ErrorHandlerOptions): StandardError {
    const opts = { ...this.defaultOptions, ...options };
    const errorMessages = errors.map(err => `${err.field}: ${err.message}`).join(', ');
    
    const standardError: StandardError = {
      type: ErrorType.VALIDATION,
      severity: ErrorSeverity.LOW,
      message: `Validacijska greška: ${errorMessages}`,
      details: errors,
      timestamp: new Date(),
      context: opts.context
    };
    
    if (opts.logError) {
      this.logError(standardError);
    }
    
    if (opts.showToast) {
      this.showErrorToast('Validacijska greška', errorMessages);
    }
    
    return standardError;
  }

  /**
   * Handles generic errors
   */
  handleGenericError(error: Error | string, options?: ErrorHandlerOptions): StandardError {
    const opts = { ...this.defaultOptions, ...options };
    const message = typeof error === 'string' ? error : error.message;
    
    const standardError: StandardError = {
      type: ErrorType.GENERIC,
      severity: opts.severity || ErrorSeverity.MEDIUM,
      message,
      details: typeof error === 'string' ? undefined : error,
      timestamp: new Date(),
      context: opts.context
    };
    
    if (opts.logError) {
      this.logError(standardError);
    }
    
    if (opts.showToast) {
      this.showErrorToast('Greška', message);
    }
    
    return standardError;
  }

  /**
   * Logs error using the logger service
   */
  private logError(error: StandardError): void {
    const logMessage = `[${error.type}] ${error.message}`;
    const logData = {
      severity: error.severity,
      code: error.code,
      details: error.details,
      context: error.context,
      timestamp: error.timestamp
    };

    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
        logger.error(logMessage, logData);
        break;
      case ErrorSeverity.HIGH:
        logger.error(logMessage, logData);
        break;
      case ErrorSeverity.MEDIUM:
        logger.warn(logMessage, logData);
        break;
      case ErrorSeverity.LOW:
        logger.info(logMessage, logData);
        break;
      default:
        logger.error(logMessage, logData);
    }
  }

  /**
   * Shows error toast to user
   */
  private showErrorToast(title: string, description: string): void {
    toast({
      title,
      description,
      variant: 'destructive',
    });
  }

  /**
   * Creates error context for better debugging
   */
  createContext(component?: string, action?: string, additionalData?: Record<string, unknown>): StandardError['context'] {
    return {
      component,
      action,
      ...additionalData,
      timestamp: new Date().toISOString()
    };
  }
}

export const errorService = new ErrorService();
